import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search, UserPlus, Sparkles, Headphones, CalendarClock, UserCheck, XCircle, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { SOURCES, StatusBadge, fmtDT, leadName, LeadDetailDialog, AssignAgentDialog, SourceIcon } from './BdcShared';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';
const emptyForm = { first_name: '', last_name: '', phone: '', email: '', source: 'Walk-In', campaign_name: '', vehicle_interest: '', message: '' };

const GROUPS = [
  { key: 'novos', label: 'Novos', desc: 'Aguardando atribuição BDC', statuses: ['NEW'], icon: Sparkles, color: 'text-sky-400', bg: 'bg-sky-400/10', border: 'border-sky-400/40', bar: 'bg-sky-400' },
  { key: 'bdc', label: 'Com BDC', desc: 'Em contato / follow-up', statuses: ['BDC_ASSIGNED', 'CONTACTING', 'FOLLOW_UP'], icon: Headphones, color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/40', bar: 'bg-primary' },
  { key: 'aguardando', label: 'Aguardando Vendedor', desc: 'Agendado / solicitado', statuses: ['APPOINTMENT_SCHEDULED', 'SALESPERSON_REQUESTED', 'AWAITING_SALES_ASSIGNMENT'], icon: CalendarClock, color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/40', bar: 'bg-amber-400' },
  { key: 'vendedor', label: 'Com Vendedor', desc: 'Atribuído ao vendedor', statuses: ['ASSIGNED_TO_SALESPERSON'], icon: UserCheck, color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/40', bar: 'bg-emerald-400' },
  { key: 'perdidos', label: 'Perdidos', desc: 'Leads perdidos', statuses: ['LOST'], icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/40', bar: 'bg-red-400' },
];

const GROUP_BAR = { novos: '#38bdf8', bdc: '#3b82f6', aguardando: '#eab308', vendedor: '#10b981', perdidos: '#ef4444' };

export const BdcPipeline = ({ initialLeadId }) => {
  const { user } = useAuth();
  const canAssignAgent = user?.permissions?.includes('assign_bdc_agent');
  const isAgent = user?.role === 'BDC Agent';
  const canAssignSp = user?.permissions?.includes('assign_salesperson');
  const canDelete = ['Owner', 'Administrator'].includes(user?.role) || (user?.role || '').endsWith('Manager');
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState('');
  const [fGroup, setFGroup] = useState('');
  const [fAgent, setFAgent] = useState('');
  const [fSource, setFSource] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(initialLeadId || null);
  const [assignLead, setAssignLead] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (fAgent) params.set('agent', fAgent);
      if (fSource) params.set('source', fSource);
      const { data } = await api.get(`/bdc-leads?${params.toString()}`);
      setLeads(data);
    } catch (e) {}
  }, [q, fAgent, fSource]);

  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);
  useEffect(() => {
    if (canAssignAgent) api.get('/bdc/agents-workload').then(({ data }) => setAgents(data)).catch(() => {});
  }, [canAssignAgent]);
  useEffect(() => { if (initialLeadId) setDetailId(initialLeadId); }, [initialLeadId]);

  const createLead = async () => {
    if (!form.first_name || (!form.phone && !form.email)) {
      toast({ title: 'Dados obrigatórios', description: 'Informe nome e um contato (telefone ou e-mail).' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/bdc-leads', form);
      toast({ title: 'Lead criado', description: data.existing_customer ? 'Existing Customer — lead vinculado ao cliente existente.' : 'Novo cliente criado. Aguardando atribuição do BDC Manager.' });
      setNewOpen(false); setForm(emptyForm); load();
    } catch (e) { toast({ title: 'Erro ao criar lead', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const groupOf = (l) => GROUPS.find((g) => g.statuses.includes(l.status));  const groupLeads = (g) => leads.filter((l) => g.statuses.includes(l.status));

  const claimLead = async (l) => {
    try {
      await api.post(`/bdc-leads/${l.id}/claim`);
      toast({ title: 'Lead é seu', description: `${leadName(l)} · ${l.source} — ligue agora.` });
      load();
    } catch (e) { toast({ title: 'Não foi possível pegar o lead', description: e.response?.data?.detail || '' }); }
  };

  const deleteLead = async (l) => {
    if (!window.confirm(`Excluir o lead "${leadName(l)}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/bdc-leads/${l.id}`);
      toast({ title: 'Lead excluído', description: leadName(l) });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: 'Erro ao excluir', description: typeof d === 'string' ? d : 'Tente novamente.' });
    }
  };
  const visibleGroups = GROUPS.filter((g) => !fGroup || g.key === fGroup);

  const LeadRow = ({ l }) => {
    const ref = l.first_contact_at || l.first_attempt_at || l.received_at;
    const mins = ref ? Math.floor((Date.now() - new Date(ref).getTime()) / 60000) : null;
    const slaCls = mins == null ? 't-label' : mins <= 60 ? 't-ok' : mins <= 240 ? 't-warn' : 't-bad';
    const closed = ['ASSIGNED_TO_SALESPERSON', 'LOST'].includes(l.status);
    return (
    <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setDetailId(l.id)} className="cursor-pointer">
      <td>
        <p className="font-medium flex items-center gap-1.5">
          <SourceIcon source={l.source} className="w-3.5 h-3.5 md:hidden" testid={`lead-source-icon-sm-${l.id}`} />
          {leadName(l)}
        </p>
        <p className="num text-[11px] t-label">{l.phone || l.email || '—'}</p>
      </td>
      <td className="hidden md:table-cell">
        <p className="text-foreground/75 flex items-center gap-1.5">
          <SourceIcon source={l.source} testid={`lead-source-icon-${l.id}`} />
          {l.source}
        </p>
        {l.campaign_name && <p className="text-[11px] t-label truncate max-w-[140px]">{l.campaign_name}</p>}
      </td>
      <td className="text-foreground/75 hidden lg:table-cell">{l.vehicle_interest || '—'}</td>
      <td className="text-foreground/75 hidden sm:table-cell">
        {l.status === 'ASSIGNED_TO_SALESPERSON' && l.salesperson_name
          ? <span className="t-ok font-medium">{l.salesperson_name}</span>
          : (l.bdc_agent_name || <span className="t-label">—</span>)}
      </td>
      <td className="hidden xl:table-cell">
        <p className="num text-[11.5px] text-foreground/75">{fmtDT(ref)}</p>
        <p className="text-[10.5px] t-label">{l.platform || l.preferred_contact || l.source}</p>
      </td>
      <td className={`num font-semibold ${closed ? 't-label' : slaCls}`} data-testid={`lead-sla-${l.id}`}>
        {closed || mins == null ? '—' : mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`}
      </td>
      <td className="text-[12px] text-foreground/60 hidden xl:table-cell">{l.next_action}</td>
      <td><StatusBadge status={l.status} /></td>
      <td className="text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1.5">
          {canAssignAgent && l.status === 'NEW' && (
            <button data-testid={`row-assign-${l.id}`} onClick={() => setAssignLead(l)} className="ent-btn ent-btn-sm text-primary border-primary/50"><UserPlus className="w-3 h-3" /> Atribuir</button>
          )}
          {isAgent && l.status === 'NEW' && !l.bdc_agent_id && (
            <button data-testid={`row-claim-${l.id}`} onClick={() => claimLead(l)} className="ent-btn ent-btn-sm text-[hsl(var(--ok))] border-[hsl(var(--ok))]/50"><UserPlus className="w-3 h-3" /> Pegar</button>
          )}
          {canDelete && (
            <button data-testid={`bdc-delete-${l.id}`} title="Excluir lead" onClick={() => deleteLead(l)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </td>
    </tr>
    );
  };

  return (
    <div className="space-y-4" data-testid="bdc-pipeline">
      {/* pipeline summary ribbon */}
      <div className="ent-ribbon" data-testid="bdc-ribbon">
        <div><p className="ent-label">Total</p><p className="ent-value mt-1" data-testid="pipe-count-total">{leads.length}</p></div>
        {GROUPS.map((g) => {
          const n = groupLeads(g).length;
          const active = fGroup === g.key;
          return (
            <button key={g.key} data-testid={`pipe-group-${g.key}`} onClick={() => setFGroup(active ? '' : g.key)}
              className={`text-left transition-colors hover:bg-primary/5 ${active ? 'bg-primary/[0.07]' : ''}`}>
              <p className="ent-label whitespace-nowrap">{g.label}</p>
              <p className="ent-value mt-1" data-testid={`pipe-count-${g.key}`}>{n}</p>
            </button>
          );
        })}
        <div>
          <p className="ent-label whitespace-nowrap">Taxa de conversão</p>
          <p className="ent-value mt-1" data-testid="pipe-conversion">
            {leads.length ? `${Math.round(groupLeads(GROUPS[3]).length / leads.length * 100)}%` : '0%'}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
            <input data-testid="pipeline-search" className="ent-input pl-8" style={{ width: 210 }} placeholder="Nome, telefone, e-mail..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {canAssignAgent && (
            <select data-testid="filter-agent" className="ent-input" style={{ width: 'auto' }} value={fAgent} onChange={(e) => setFAgent(e.target.value)}>
              <option value="" className="bg-popover">Todos os agentes</option>
              {agents.map((a) => <option key={a.user_id} value={a.user_id} className="bg-popover">{a.name}</option>)}
            </select>
          )}
          <select data-testid="filter-source" className="ent-input" style={{ width: 'auto' }} value={fSource} onChange={(e) => setFSource(e.target.value)}>
            <option value="" className="bg-popover">Todas as fontes</option>
            {SOURCES.map((s) => <option key={s} className="bg-popover">{s}</option>)}
          </select>
          {fGroup && (
            <button data-testid="clear-group-filter" onClick={() => setFGroup('')} className="ent-btn ent-btn-sm">Limpar filtro ✕</button>
          )}
        </div>
        <button data-testid="new-bdc-lead-btn" onClick={() => setNewOpen(true)} className="ent-btn ent-btn-primary"><Plus className="w-3.5 h-3.5" /> Novo Lead</button>
      </div>

      {/* grouped sections */}
      {visibleGroups.map((g) => {
        const gl = groupLeads(g);
        if (gl.length === 0 && fGroup !== g.key) return null;
        const Icon = g.icon;
        return (
          <div key={g.key} data-testid={`pipe-section-${g.key}`} className="ent-panel overflow-hidden">
            <div className="ent-colhead" style={{ borderTop: `2px solid ${GROUP_BAR[g.key]}` }}>
              <Icon className="w-3.5 h-3.5" style={{ color: GROUP_BAR[g.key] }} />
              <span className="ent-coltitle uppercase tracking-wide">{g.label}</span>
              <span className="ent-pill">{gl.length}</span>
              <span className="text-[11px] t-label ml-auto hidden sm:block">{g.desc}</span>
            </div>
            <div className="overflow-x-auto">
              {gl.length === 0 ? (
                <p className="ent-empty">Nenhum lead neste grupo</p>
              ) : (
                <table className="ent-table">
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th className="hidden md:table-cell">Fonte / campanha</th>
                      <th className="hidden lg:table-cell">Veículo de interesse</th>
                      <th className="hidden sm:table-cell">{g.key === 'vendedor' ? 'Vendedor' : 'Resp. BDC'}</th>
                      <th className="hidden xl:table-cell">Último contato</th>
                      <th>SLA</th>
                      <th className="hidden xl:table-cell">Próxima ação</th>
                      <th>Status</th>
                      <th className="text-right">Ação</th>
                    </tr>
                  </thead>
                  <tbody>{gl.map((l) => <LeadRow key={l.id} l={l} />)}</tbody>
                </table>
              )}
            </div>
          </div>
        );
      })}
      {leads.length === 0 && (
        <div className="rounded-xl border border-border bg-card text-center py-14 text-muted-foreground">Nenhum lead encontrado.</div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-lg">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Novo Lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Nome</label><input data-testid="nl-first" className={inputCls} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Sobrenome</label><input data-testid="nl-last" className={inputCls} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Telefone</label><input data-testid="nl-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="781-555-0000" /></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">E-mail</label><input data-testid="nl-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Fonte</label><select data-testid="nl-source" className={inputCls} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>{SOURCES.map((s) => <option key={s} className="bg-popover">{s}</option>)}</select></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Campanha (opcional)</label><input className={inputCls} value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} /></div>
            <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Veículo de interesse</label><input data-testid="nl-vehicle" className={inputCls} value={form.vehicle_interest} onChange={(e) => setForm({ ...form, vehicle_interest: e.target.value })} placeholder="Ex: 2025 Toyota RAV4" /></div>
            <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Mensagem</label><textarea className={inputCls} rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setNewOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
            <button data-testid="nl-save" onClick={createLead} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Criar Lead'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadDetailDialog leadId={detailId} open={!!detailId} onClose={() => setDetailId(null)} canAssignAgent={canAssignAgent} canAssignSalesperson={canAssignSp} currentUserId={user?.user_id} onChanged={load} />
      <AssignAgentDialog lead={assignLead} open={!!assignLead} onClose={() => setAssignLead(null)} onDone={load} />
    </div>
  );
};
